import React, { useEffect, useState } from 'react';
import { Factory, Star, Tag } from 'lucide-react';
import { useApp } from '../AppProvider';
import { useClientTagStore } from '../../lib/stores/clientTagStore';
import { useSettoriStore } from '../../lib/stores/settoriStore';
import { useInterestCategoryStore } from '../../lib/stores/interestCategoryStore';
import { InterestTreeChipRow, MultiChipRow, ValueRow } from '../shared/property-rows';

interface LeadClassificationRowsProps {
  selectedClient: any;
  shouldHideTags: boolean;
  canEdit?: boolean;
  onLeadUpdate?: (id: string, updates: any) => Promise<boolean>;
}

const rowIcon = (Icon: React.ComponentType<any>) => <Icon className="h-3 w-3" strokeWidth={1.5} />;

const chipDate = (iso?: string | null) =>
  iso
    ? `Assegnato il ${new Date(iso).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' })}`
    : '';

function ReadOnlyChips({ items }: { items: { key: string; label: string; color?: string | null; hint?: string }[] }) {
  if (items.length === 0) {
    return <span className="text-[rgba(55,53,47,0.45)] dark:text-gray-500">Vuoto</span>;
  }
  return (
    <>
      {items.map((it) => (
        <span
          key={it.key}
          title={it.hint}
          className="inline-flex items-center rounded px-1.5 py-0.5 text-[11.5px] font-medium"
          style={{ backgroundColor: it.color ? `${it.color}20` : 'rgba(55,53,47,0.08)', color: it.color || undefined }}
        >
          <span className="text-[#37352F] dark:text-white">{it.label}</span>
        </span>
      ))}
    </>
  );
}

/**
 * Classificazione del lead/cliente come righe SEMPRE visibili (Tag, Settore,
 * Interessi): chip in stile drawer con selezione inline a salvataggio
 * immediato (aggiungi dal dropdown ricercabile, rimuovi dalla X; i tag si
 * creano anche al volo). Data di assegnazione nel tooltip del chip.
 */
export function LeadClassificationRows({ selectedClient, shouldHideTags, canEdit = false, onLeadUpdate }: LeadClassificationRowsProps) {
  const { toast } = useApp();
  const { tags: allTags, fetchTags, createTag } = useClientTagStore();
  const { settori: allSettori, fetchSettori } = useSettoriStore();
  const { categories: interestCategories, fetchCategories } = useInterestCategoryStore();

  useEffect(() => {
    if (!canEdit) return;
    fetchTags();
    fetchSettori();
    fetchCategories();
  }, [canEdit, fetchTags, fetchSettori, fetchCategories]);

  const tags: any[] = selectedClient.tags || [];
  const settori: any[] = selectedClient.settori || [];
  const interests: any[] = selectedClient.interests || [];

  const tagIds = tags.map((t) => t.id);
  const settoriIds = settori.map((s) => s.settore_id || s.settore?.id).filter(Boolean);
  const interestIds = interests
    .map((i) => i.interest_category_id || i.interest_category?.id)
    .filter(Boolean);

  // Optimistic: il refetch post-update arriva con lag, i chip rispondono subito
  const [pendingTags, setPendingTags] = useState<string[] | null>(null);
  const [pendingSettori, setPendingSettori] = useState<string[] | null>(null);
  const [pendingInterests, setPendingInterests] = useState<string[] | null>(null);

  useEffect(() => { setPendingTags(null); }, [tagIds.join(',')]);
  useEffect(() => { setPendingSettori(null); }, [settoriIds.join(',')]);
  useEffect(() => { setPendingInterests(null); }, [interestIds.join(',')]);

  const save = (
    key: 'selected_tags' | 'selected_settori' | 'selected_interest_categories',
    setPending: (v: string[] | null) => void,
  ) => async (ids: string[]) => {
    setPending(ids);
    const ok = await onLeadUpdate?.(selectedClient.id, { [key]: ids });
    if (!ok) {
      setPending(null);
      toast.error("Errore durante l'aggiornamento");
    }
  };

  const saveTags = save('selected_tags', setPendingTags);
  const saveSettori = save('selected_settori', setPendingSettori);
  const saveInterests = save('selected_interest_categories', setPendingInterests);

  const currentTags = pendingTags ?? tagIds;
  const currentSettori = pendingSettori ?? settoriIds;
  const currentInterests = pendingInterests ?? interestIds;

  const handleCreateTag = async (label: string) => {
    const created = await createTag({ name: label, color: '#E91E8C' });
    if (created) await saveTags([...currentTags, created.id]);
    else toast.error('Errore nella creazione del tag');
  };

  // Le opzioni includono anche i chip già assegnati non presenti nello store
  // (es. tag disattivato, elenco non ancora caricato): mai mostrare id grezzi.
  const mergeOptions = (
    fromStore: { value: string; label: string; color?: string | null }[],
    current: { value: string; label: string; color?: string | null }[],
  ) => {
    const seen = new Set(fromStore.map((o) => o.value));
    return [...fromStore, ...current.filter((o) => o.value && !seen.has(o.value))];
  };

  const tagOptions = mergeOptions(
    allTags.filter((t: any) => t.is_active !== false).map((t: any) => ({ value: t.id, label: t.name, color: t.color })),
    tags.map((t) => ({ value: t.id, label: t.name, color: t.color })),
  );
  const settoriOptions = mergeOptions(
    allSettori.map((s: any) => ({ value: s.id, label: s.name, color: s.color || '#6B7280' })),
    settori.map((s) => ({ value: s.settore_id || s.settore?.id, label: s.settore?.name || 'Settore', color: s.settore?.color || '#6B7280' })),
  );
  const interestOptions = mergeOptions(
    interestCategories.map((c: any) => ({ value: c.id, label: c.name, color: c.color || '#3B82F6' })),
    interests.map((i) => ({ value: i.interest_category_id || i.interest_category?.id, label: i.interest_category?.name || 'Interesse', color: i.interest_category?.color || '#3B82F6' })),
  );

  const tagHints: Record<string, string> = {};
  tags.forEach((t) => { const d = chipDate(t.assigned_at); if (d) tagHints[t.id] = d; });
  const interestHints: Record<string, string> = {};
  interests.forEach((i) => {
    const key = i.interest_category_id || i.interest_category?.id;
    const d = chipDate(i.created_at);
    if (key && d) interestHints[key] = d;
  });

  if (!canEdit || !onLeadUpdate) {
    return (
      <div>
        {!shouldHideTags && (
          <ValueRow icon={rowIcon(Tag)} label="Tag" wrap>
            <ReadOnlyChips items={tags.map((t) => ({ key: t.id, label: t.name, color: t.color, hint: chipDate(t.assigned_at) }))} />
          </ValueRow>
        )}
        <ValueRow icon={rowIcon(Factory)} label="Settore" wrap>
          <ReadOnlyChips items={settori.map((s) => ({ key: s.settore_id || s.id, label: s.settore?.name || 'Settore', color: s.settore?.color || '#6B7280' }))} />
        </ValueRow>
        <ValueRow icon={rowIcon(Star)} label="Interessi" wrap>
          <ReadOnlyChips items={interests.map((i) => ({ key: i.interest_category_id || i.interest_category?.id, label: i.interest_category?.name || 'Interesse', color: i.interest_category?.color || '#3B82F6', hint: chipDate(i.created_at) }))} />
        </ValueRow>
      </div>
    );
  }

  return (
    <div>
      {!shouldHideTags && (
        <MultiChipRow
          icon={rowIcon(Tag)}
          label="Tag"
          selected={currentTags}
          options={tagOptions}
          onAdd={(v) => saveTags([...currentTags, v])}
          onRemove={(v) => saveTags(currentTags.filter((x) => x !== v))}
          onCreate={handleCreateTag}
          hints={tagHints}
        />
      )}

      <MultiChipRow
        icon={rowIcon(Factory)}
        label="Settore"
        selected={currentSettori}
        options={settoriOptions}
        onAdd={(v) => saveSettori([...currentSettori, v])}
        onRemove={(v) => saveSettori(currentSettori.filter((x) => x !== v))}
      />

      <InterestTreeChipRow
        selected={currentInterests}
        onChange={saveInterests}
        hints={interestHints}
        extraOptions={interestOptions}
      />
    </div>
  );
}
